import React, { useState, useEffect } from 'react';
import { ChevronLeft, FileText, Truck, BarChart3 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { supervisorsAPI } from '../../services/api';
import { getDisplayStatus, hideImported } from '../../utils/displayName';
import { toast } from 'react-toastify';

const SupervisorAgentActivity = () => {
	const { agentId } = useParams();
	const navigate = useNavigate();
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState('pending');

	useEffect(() => {
		if (!agentId) return;
		const fetchData = async () => {
			try {
				setLoading(true);
				const res = await supervisorsAPI.getAgentActivity(agentId);
				setData(res);
			} catch (err) {
				toast.error(err.response?.data?.message || 'Failed to load agent activity');
				setData(null);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, [agentId]);

	if (loading && !data) {
		return (
			<div className='h-full flex items-center justify-center'>
				<p className='text-white/60'>Loading...</p>
			</div>
		);
	}

	if (!data) {
		return (
			<div className='h-full flex flex-col p-6'>
				<button type='button' onClick={() => navigate('/supervisor/dashboard')} className='glass-card p-3 rounded-xl self-start'>
					<ChevronLeft className='w-5 h-5 text-white' />
				</button>
				<p className='text-white/60 mt-4'>Could not load agent.</p>
			</div>
		);
	}

	const { agent, assignedBills, recentDeliveries } = data;
	const name = agent ? [agent.firstName, agent.lastName].filter(Boolean).join(' ').trim() || agent.email || '—' : '—';
	const pendingBills = (assignedBills || []).filter((b) => b.status === 'pending' || b.status === 'overdue');
	const totalCollected = agent != null ? (Number(agent.totalCollected ?? agent.total_collected) || 0) : 0;

	const tabs = [
		{ id: 'pending', label: 'Pending Bills', count: pendingBills.length, icon: FileText },
		{ id: 'delivered', label: 'Delivered Bills', count: recentDeliveries?.length ?? 0, icon: Truck },
		{ id: 'performance', label: 'Performance', icon: BarChart3 },
	];

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-[#001E3C] border-b border-white/10'>
				<div className='flex items-center gap-4'>
					<button type='button' onClick={() => navigate('/supervisor/dashboard')} className='glass-card p-3 rounded-xl'>
						<ChevronLeft className='w-5 h-5 text-white' />
					</button>
					<div>
						<h2 className='text-xl font-black uppercase text-white'>{name}</h2>
						<p className='text-sm text-white/60'>{agent.email}</p>
					</div>
				</div>
			</header>
			<div className='flex border-b border-white/10 bg-white/5'>
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type='button'
						onClick={() => setActiveTab(tab.id)}
						className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-bold transition-colors ${
							activeTab === tab.id ? 'text-[#00F5FF] border-b-2 border-[#00F5FF] bg-white/5' : 'text-white/60 hover:text-white'
						}`}
					>
						<tab.icon className='w-4 h-4' />
						{tab.label}
						{tab.count != null && tab.count > 0 && <span className='bg-white/20 rounded-full px-1.5 text-xs'>{tab.count}</span>}
					</button>
				))}
			</div>
			<main className='flex-1 overflow-y-auto p-6 no-scrollbar'>
				{activeTab === 'pending' && (
					<section>
						<h3 className='font-black text-white mb-3'>Pending bills ({pendingBills.length})</h3>
						{pendingBills.length ? (
							<div className='space-y-2'>
								{pendingBills.map((b) => (
									<div key={b.id} className='glass-card p-3 flex justify-between items-center'>
										<div>
											<p className='font-mono text-white'>{b.valuationNo || b.billNo}</p>
											<p className='text-sm text-white/60'>{b.ownerName || '—'} · {getDisplayStatus(b.status)}</p>
										</div>
										<p className='text-[#FFC107] font-bold'>₵{(Number(b.totalDue) || 0).toLocaleString()}</p>
									</div>
								))}
							</div>
						) : (
							<p className='text-white/50'>No pending bills</p>
						)}
					</section>
				)}
				{activeTab === 'delivered' && (
					<section>
						<h3 className='font-black text-white mb-3'>Delivered bills ({recentDeliveries?.length ?? 0})</h3>
						{recentDeliveries?.length ? (
							<div className='space-y-2'>
								{recentDeliveries.map((d) => (
									<div key={d.id} className='glass-card p-3'>
										<p className='font-mono text-white'>{d.property?.valuationNo || d.property?.billNo}</p>
										<p className='text-sm text-white/60'>{hideImported(d.property?.ownerName)} · {d.deliveryDate ? new Date(d.deliveryDate).toLocaleDateString() : ''}</p>
									</div>
								))}
							</div>
						) : (
							<p className='text-white/50'>No deliveries yet</p>
						)}
					</section>
				)}
				{activeTab === 'performance' && (
					<section className='space-y-4'>
						<h3 className='font-black text-white mb-3'>Performance</h3>
						<div className='grid grid-cols-2 gap-4'>
							<div className='glass-card p-4'>
								<p className='text-xs text-white/50 uppercase tracking-wide'>Total delivered</p>
								<p className='text-2xl font-black text-[#00E676]'>{agent.totalDelivered ?? 0}</p>
							</div>
							<div className='glass-card p-4'>
								<p className='text-xs text-white/50 uppercase tracking-wide'>Total collected (₵)</p>
								<p className='text-2xl font-black text-[#FFC107]'>{totalCollected.toLocaleString()}</p>
							</div>
							<div className='glass-card p-4 col-span-2'>
								<p className='text-xs text-white/50 uppercase tracking-wide'>Assigned bills (pending + delivered)</p>
								<p className='text-2xl font-black text-white'>{assignedBills?.length ?? 0}</p>
							</div>
							{agent.assignedArea && (
								<div className='glass-card p-4 col-span-2'>
									<p className='text-xs text-white/50 uppercase tracking-wide'>Assigned area</p>
									<p className='font-bold text-white'>{agent.assignedArea}</p>
								</div>
							)}
						</div>
					</section>
				)}
			</main>
		</div>
	);
};

export default SupervisorAgentActivity;
